import React, { useEffect, useState } from 'react';

import { Alert, AlertDescription } from 'components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';

interface PaceStatistic {
  question_flow?: string;
  previous_path?: string;
  median_response_time_s: number;
  ninetieth_perc_response_time_s: number;
}

interface PaceReportProps {
  organizationId?: string;
}

export const PaceReport: React.FC<PaceReportProps> = ({ organizationId }) => {
  const [data, setData] = useState<PaceStatistic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        if (!isMounted) {
          return;
        }

        setData([]);
        setError(null);
      } catch (err) {
        console.error('Failed to load pace stats', err);
        if (isMounted) {
          setError('Unable to load pace statistics right now.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [organizationId]);

  if (loading) {
    return (
      <div className="flex min-h-[200px] w-full items-center justify-center">
        <LoadingAnimation />
      </div>
    );
  }

  return (
    <Card className="bg-card/90">
      <CardHeader>
        <CardTitle className="text-lg font-semibold">Course Pace Overview</CardTitle>
        <p className="text-sm text-muted-foreground">
          Median and 90th percentile response times per module.
        </p>
      </CardHeader>
      <CardContent>
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pace statistics available yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Module</TableHead>
                  <TableHead className="text-right">Median Response (s)</TableHead>
                  <TableHead className="text-right">90th Percentile (s)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium capitalize">
                      {row.question_flow || row.previous_path || 'Unknown module'}
                    </TableCell>
                    <TableCell className="text-right">
                      {Math.round(row.median_response_time_s)}
                    </TableCell>
                    <TableCell className="text-right">
                      {Math.round(row.ninetieth_perc_response_time_s)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
